import * as React from 'react'
import { useDispatch } from 'react-redux'
import { useSearch } from '@tanstack/react-router'
import { TranslationState, WordTranslationLine } from 'lib/interfaces'
import {
   resultIsThaiWord,
   QueryResult,
   resultIsEngWord,
   resultIsSearch,
   resultIsEmpty,
   resultIsEmptyQuery,
   resultIsTranslation,
} from 'models/query-result.model'
import { setHighlightedWord } from 'store/tooltip.reducer'
import { isNumericRegex } from 'lib/generic-utils/strings'
import EngDictionaryPage from 'components/dictionary/english/eng-dictionary-page'
import ThaiDictionaryPage from 'components/dictionary/thai/thai-dictionary-page'
import EnglishSearchResults from 'components/search/english-search-results'
import ThaiWordListItem from 'components/dictionary/thai/list/thai-word-list-item'
import TranslationView from 'components/homepage/translation/translation-view'
import { useEffect, useMemo } from 'react'
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form'
import { T2EFormData } from 'hooks/useTranslationForm'
import { preprocessQuery } from 'lib/preprocessQuery'
import QueryResultContainer from 'components/query-result/query-result-container'

interface Props {
   setValue: UseFormSetValue<T2EFormData>
   getValues: UseFormGetValues<T2EFormData>
   queryResult: QueryResult
   preprocessedQuery: string
   setTranslationState: (value: TranslationState) => void
}

const QueryResults = ({
   setValue,
   getValues,
   queryResult,
   preprocessedQuery,
   setTranslationState,
}: Props) => {
   const dispatch = useDispatch()
   const search = useSearch({ strict: false }) as Record<string, unknown>

   // Derived, not copied: the lines always come straight from the result.
   const translationLines = useMemo<WordTranslationLine[]>(
      () => (resultIsTranslation(queryResult) ? (queryResult.thaiTranslationLines ?? []) : []),
      [queryResult]
   )

   const queryWord = search.word as string
   const queryQ = search.q as string

   // Update the textbox when q changes, e.g. clicking a word in the tooltip
   useEffect(() => {
      dispatch(setHighlightedWord({ highlightedWord: undefined }))

      // Don't update the value if it's a Thai id
      if (isNumericRegex.test(queryQ)) return

      // What's in the box already produced this query — it's the paste that was
      // just submitted, not a query that arrived from somewhere else. Leave it
      // alone: a query trimmed to MAX_QUERY_LENGTH would otherwise overwrite the
      // longer text it came from, throwing away the part the notice is telling
      // the user to split off and search separately.
      if (preprocessQuery(getValues('sourceText') ?? '') === (queryQ ?? '')) return

      setValue('sourceText', (queryQ as string) ?? '')
   }, [dispatch, getValues, queryQ, setValue])

   useEffect(() => {
      if (!queryQ) return
      if (queryWord === queryQ) return

      setTranslationState(TranslationState.Shown)
   }, [queryQ, queryWord, setTranslationState])

   return (
      <>
         {resultIsEngWord(queryResult) && (
            <EngDictionaryPage
               engDictionaryWord={queryResult.engDictionaryWord}
               exampleSentences={queryResult.exampleSentences}
               exampleSentencesByMeaningId={queryResult.exampleSentencesByMeaningId}
               translations={queryResult.engDictionaryTranslations}
            />
         )}

         {resultIsThaiWord(queryResult) && (
            <ThaiDictionaryPage
               dictionaryWord={queryResult.dictionaryWord}
               exampleSentences={queryResult.exampleSentences}
               exampleSentencesByMeaningId={queryResult.exampleSentencesByMeaningId}
            />
         )}

         {resultIsSearch(queryResult) && Boolean(queryResult.engMatches.length) && (
            <QueryResultContainer className="mt-20">
               <EnglishSearchResults
                  engWords={queryResult.engMatches}
                  translations={queryResult.translations}
               />
            </QueryResultContainer>
         )}

         {resultIsEmptyQuery(queryResult) && (
            <QueryResultContainer as="div" className="mt-20 text-center">
               Please enter some Thai text to translate.
            </QueryResultContainer>
         )}

         {resultIsEmpty(queryResult) && (
            <QueryResultContainer as="div" className="mt-20 text-center">
               We're sorry, we could find any matches for your search "<b>{preprocessedQuery}</b>".
            </QueryResultContainer>
         )}

         {resultIsSearch(queryResult) && Boolean(queryResult.translitMatches.length) && (
            <QueryResultContainer as="div" className="mt-20 thai-results-compat translit-results">
               The following Thai words matched the transliteration "<b>{preprocessedQuery}</b>"
               <br />
               <br />
               <ul className="examplesContainer">
                  {queryResult.translitMatches.map((thisThaiWord, index) => (
                     <ThaiWordListItem key={index} word={thisThaiWord} />
                  ))}
               </ul>
            </QueryResultContainer>
         )}

         {resultIsTranslation(queryResult) && translationLines.length > 0 && (
            <QueryResultContainer as="div" className="mt-3">
               {queryResult.translationUnavailable && (
                  <div className="mt-[10px] mb-[14px] rounded-[8px] p-[12px_18px_13px] bg-[#fdf6e7]">
                     <div className="text-[11px] font-bold tracking-[0.7px] uppercase text-[#b9873a]">
                        Translation is busy right now
                     </div>
                     <div className="mt-[5px] text-[15px] text-bodyBlack pretty">
                        Showing the word-by-word breakdown without an English translation — try
                        again in a little while for the full translation.
                     </div>
                  </div>
               )}
               <TranslationView lines={translationLines} queryResult={queryResult} />
            </QueryResultContainer>
         )}

         {resultIsTranslation(queryResult) && queryResult.model && (
            <div className="text-right" style={{ fontSize: '10px', opacity: 0.35 }}>
               {queryResult.model}
            </div>
         )}
      </>
   )
}

export default QueryResults
